import { useParams, Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Calendar, ArrowLeft, Tag, BookOpen } from 'lucide-react';

import { PublicShell } from '@/components/public-shell';
import { fetchJson } from '@/lib/queryClient';
import { BlogContent } from '@/components/blog-editor';
import { Badge } from '@/components/ui/badge';
import {
  getNewsletterSeriesKey,
  getNewsletterSeriesName,
  isInternalNewsletterTag,
} from '@/lib/newsletter-series';

interface BlogPostDetail {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  featuredImageUrl: string | null;
  publishedAt: string | null;
  categoryId: string | null;
  tags: string[];
  regionId: string | null;
  localityId: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  authorFirstName: string | null;
  authorLastName: string | null;
  categoryName: string | null;
  categorySlug: string | null;
}

interface BlogPostSummary {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImageUrl: string | null;
  publishedAt: string | null;
  categoryId: string | null;
  tags: string[];
  regionId: string | null;
  localityId: string | null;
  isPinned: boolean;
  authorFirstName: string | null;
  authorLastName: string | null;
  categoryName: string | null;
  categorySlug: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatArchiveDate(iso: string | null): string {
  if (!iso) return 'Undated';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

export default function UpdateDetail() {
  const { regionSlug, localitySlug, slug } = useParams<{
    regionSlug: string;
    localitySlug?: string;
    slug: string;
  }>();

  const backHref = localitySlug
    ? `/regions/${regionSlug}/${localitySlug}/updates`
    : `/regions/${regionSlug}/updates`;

  const {
    data: post,
    isLoading,
    error,
  } = useQuery<BlogPostDetail>({
    queryKey: ['/api/blog', slug],
    queryFn: () => fetchJson<BlogPostDetail>(`/api/blog/${slug}`),
  });

  const isNewsletter = post?.categorySlug === 'newsletter';
  const { data: newsletterArchive = [] } = useQuery<BlogPostSummary[]>({
    queryKey: ['/api/blog/newsletter-archive', post?.categoryId, post?.regionId, post?.localityId],
    queryFn: async () => {
      if (!post?.categoryId) return [];
      const params = new URLSearchParams({
        categoryId: post.categoryId,
        limit: '80',
        offset: '0',
      });
      if (post.localityId) {
        params.set('localityId', post.localityId);
      } else if (post.regionId) {
        params.set('regionId', post.regionId);
      }
      return fetchJson<BlogPostSummary[]>(`/api/blog?${params}`);
    },
    enabled: isNewsletter && !!post?.categoryId && !!(post?.localityId || post?.regionId),
  });

  if (isLoading) {
    return (
      <PublicShell routeKey="update-detail">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 space-y-4 animate-pulse">
          <div className="h-8 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </PublicShell>
    );
  }

  if (error || !post) {
    return (
      <PublicShell routeKey="update-detail">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center space-y-2">
          <p className="text-lg font-medium">Post not found</p>
          <Link href={backHref} className="text-sm text-primary hover:underline">
            Back to Updates
          </Link>
        </div>
      </PublicShell>
    );
  }

  const authorName =
    post.authorFirstName || post.authorLastName
      ? `${post.authorFirstName ?? ''} ${post.authorLastName ?? ''}`.trim()
      : null;
  const currentNewsletterSeriesKey = post ? getNewsletterSeriesKey(post) : null;
  const newsletterSeriesName = post ? getNewsletterSeriesName(post) : 'Newsletter Archive';
  const seriesArchive = currentNewsletterSeriesKey
    ? newsletterArchive.filter(
        issue => getNewsletterSeriesKey(issue) === currentNewsletterSeriesKey
      )
    : [];
  const showNewsletterArchive = isNewsletter && seriesArchive.length > 0;
  const visibleTags = (post.tags ?? []).filter(tag => !isInternalNewsletterTag(tag));

  return (
    <PublicShell routeKey="update-detail">
      <div
        className={
          showNewsletterArchive
            ? 'mx-auto grid max-w-6xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)]'
            : 'mx-auto max-w-3xl px-4 py-8 sm:px-6'
        }
      >
        {showNewsletterArchive && (
          <aside className="order-2 lg:order-1 lg:sticky lg:top-[calc(var(--shell-header-height)+1.5rem)] lg:max-h-[calc(100dvh-var(--shell-header-height)-3rem)] lg:self-start lg:overflow-y-auto lg:overscroll-contain lg:pr-1">
            <Link
              href={backHref}
              className="mb-5 hidden items-center gap-1 text-sm text-muted-foreground hover:text-foreground lg:inline-flex"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Updates
            </Link>

            <div className="rounded-xl border bg-card p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <BookOpen className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{newsletterSeriesName}</p>
                  <p className="text-xs text-muted-foreground">Previous issues</p>
                </div>
              </div>

              <nav className="space-y-2" aria-label="Newsletter archive">
                {seriesArchive.map(issue => {
                  const isCurrent = issue.id === post.id;
                  return (
                    <Link key={issue.id} href={`${backHref}/${issue.slug}`}>
                      <div
                        className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                          isCurrent
                            ? 'border-primary/40 bg-primary/10 text-primary'
                            : 'border-transparent hover:border-border hover:bg-muted/60'
                        }`}
                      >
                        <p className="font-medium leading-snug">
                          {formatArchiveDate(issue.publishedAt)}
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {issue.title}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>
        )}

        <article className={showNewsletterArchive ? 'order-1 min-w-0 lg:order-2' : undefined}>
          {/* Back link */}
          {!showNewsletterArchive && (
            <Link
              href={backHref}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Updates
            </Link>
          )}

          {/* Featured image */}
          {post.featuredImageUrl && (
            <div className="rounded-xl overflow-hidden mb-6 aspect-video">
              <img
                src={post.featuredImageUrl}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Category badge */}
          {post.categoryName && (
            <Badge variant="secondary" className="mb-3">
              {post.categoryName}
            </Badge>
          )}

          {/* Title */}
          <h1 className="text-3xl font-bold leading-tight mb-3">{post.title}</h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-6 pb-6 border-b border-border">
            {post.publishedAt && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(post.publishedAt)}
              </span>
            )}
            {authorName && <span>by {authorName}</span>}
          </div>

          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">{post.excerpt}</p>
          )}

          {/* Content */}
          <BlogContent html={post.content} />

          {/* Tags */}
          {visibleTags.length > 0 && (
            <div className="mt-8 pt-6 border-t border-border flex flex-wrap gap-2">
              <Tag className="h-4 w-4 text-muted-foreground mt-0.5" />
              {visibleTags.map(tag => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Bottom nav */}
          <div className="mt-8 pt-6 border-t border-border">
            <Link
              href={backHref}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Updates
            </Link>
          </div>
        </article>
      </div>
    </PublicShell>
  );
}
